import React, { useState, useEffect, useCallback } from 'react';
import { FaCheck, FaTimes, FaPencilAlt, FaTrash, FaChevronDown, FaChevronUp, FaFacebook, FaTwitter, FaWhatsapp, FaPhone, FaTelegram, FaDiscord, FaMicrosoft, FaEnvelope, FaPlug, FaDatabase, FaTicketAlt, FaRobot, FaCogs, FaMoneyBillWave, FaBrain } from 'react-icons/fa';
import AxiosInstance from 'axiosConfig';
import DialogBox from './designer_components/Dialog';
import ChannelConfig from './ChannelConfig';

const IntegrationsConfig = () => {
  // State management
  const [activeTab, setActiveTab] = useState('channels');
  const [expandedChannels, setExpandedChannels] = useState({});
  const [allowedParams, setAllowedParams] = useState({});
  const [savedParams, setSavedParams] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [scriptCode, setScriptCode] = useState('');

  // Dialog state
  const [dialog, setDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    status: 'info',
    content: null,
    onConfirm: null,
  });

  // Memoized dialog handlers
  const showDialog = useCallback((title, message = '', status = 'info', content = null, onConfirm = null) => {
    setDialog({ isOpen: true, title, message, status, content, onConfirm });
  }, []);

  const closeDialog = useCallback(() => {
    setDialog(prev => ({ ...prev, isOpen: false, content: null }));
  }, []);

  // Data fetching
  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const response = await AxiosInstance.get('/channel-configs');
        setAllowedParams(response.data.allowed_params);
        setSavedParams(response.data.channels);
        setLoading(false);
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch configurations';
        setError(errorMessage);
        setLoading(false);
        showDialog('Error', errorMessage, 'error');
      }
    };
    fetchConfigs();
  }, [showDialog]);

  // Channel expansion toggle
  const toggleChannel = useCallback((channelId) => {
    setExpandedChannels(prev => ({ ...prev, [channelId]: !prev[channelId] }));
  }, []);

  // Website URL input handler
  const handleWebsiteUrlChange = useCallback((e) => {
    setWebsiteUrl(e.target.value);
  }, []);

  // Website URL validation (called only on button click)
  const validateUrl = useCallback((url) => {
    if (!url) return false;
    return /^https?:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/.test(url);
  }, []);

  // Website linking function
  const linkWebsite = useCallback(async () => {
    if (!websiteUrl.trim()) {
      showDialog('Warning', 'Please enter a website URL.', 'warning');
      return;
    }

    if (!validateUrl(websiteUrl)) {
      showDialog('Error', 'Please enter a valid website URL (e.g., https://www.example.com).', 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await AxiosInstance.get(`/link-site/${websiteUrl.replace(/^https?:\/\//, '')}`);
      if (response.data.status === 'success') {
        setScriptCode(response.data.message);
        setWebsiteUrl('');
        showDialog('Success', 'Website linked successfully! Add the following script to your website to integrate:', 'success');
      } else {
        showDialog('Error', 'Failed to link website.', 'error');
      }
    } catch (err) {
      showDialog('Error', `Error linking website: ${err.response?.data?.message || err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [websiteUrl, validateUrl, showDialog]);

  // Parameter saving
  const saveParams = useCallback(async (channelId) => {
    const config = document.getElementById(`${channelId}-config`)?.value;
    const account = document.getElementById(`${channelId}-account`)?.value;
    const token = document.getElementById(`${channelId}-token`)?.value;

    if (!config || !account || !token) {
      showDialog('Warning', 'Please fill all fields before saving.', 'warning');
      return;
    }

    const isValid = !token.toLowerCase().includes('expired');
    const newParam = { configType: config, accountNumber: account, token, valid: isValid };

    try {
      await AxiosInstance.post('/save-channel-param', { channelId, ...newParam });
      setSavedParams(prev => ({
        ...prev,
        [channelId]: [...(prev[channelId] || []), newParam],
      }));
      document.getElementById(`${channelId}-config`).value = Object.keys(allowedParams[channelId] || {})[0] || '';
      document.getElementById(`${channelId}-account`).value = '';
      document.getElementById(`${channelId}-token`).value = '';
      showDialog('Success', 'Parameter saved successfully!', 'success');
    } catch (err) {
      showDialog('Error', `Error saving parameter: ${err.response?.data?.message || err.message}`, 'error');
    }
  }, [allowedParams, showDialog]);

  // Parameter editing
  const editParam = useCallback((channelId, index) => {
    const param = savedParams[channelId]?.[index];
    if (!param || !param.configType || !param.accountNumber) {
      showDialog('Error', 'Invalid parameter data. Please try again.', 'error');
      return;
    }
    showDialog(
      '',
      '',
      'info',
      <ChannelConfig
        channel_name={channelId}
        channel_par={param.configType}
        channel_ext_ref={param.accountNumber}
      />,
      () => {
        setSavedParams(prev => ({
          ...prev,
          [channelId]: prev[channelId].filter((_, i) => i !== index),
        }));
      }
    );
  }, [savedParams, showDialog]);

  // Parameter deletion
  const deleteParam = useCallback((channelId, index) => {
    const param = savedParams[channelId][index];
    showDialog(
      'Confirm Delete',
      `Are you sure you want to delete the configuration for ${param.accountNumber}?`,
      'warning',
      null,
      async () => {
        try {
          await AxiosInstance.delete('/delete-channel-param', {
            data: { channelId, configType: param.configType, accountNumber: param.accountNumber },
          });
          setSavedParams(prev => ({
            ...prev,
            [channelId]: prev[channelId].filter((_, i) => i !== index),
          }));
          showDialog('Success', 'Parameter deleted successfully!', 'success');
        } catch (err) {
          showDialog('Error', `Error deleting parameter: ${err.response?.data?.message || err.message}`, 'error');
        }
      }
    );
  }, [savedParams, showDialog]);

  // Memoized ChannelItem component
  const ChannelItem = React.memo(({ channelId, title, icon: Icon, color }) => {
    const configOptions = allowedParams[channelId]
      ? Object.entries(allowedParams[channelId]).map(([key, label]) => ({ value: key, label }))
      : [];
    const hasSavedParams = savedParams[channelId]?.length > 0;

    return (
      <section className="border-b pb-4">
        <header
          className="flex justify-between items-center cursor-pointer py-4 px-6"
          onClick={() => toggleChannel(channelId)}
        >
          <div className="flex items-center space-x-2">
            <Icon className={color} />
            <h3 className="text-lg font-medium">{title}</h3>
            {hasSavedParams && <FaCheck className="text-green-500" />}
          </div>
          {expandedChannels[channelId] ? (
            <FaChevronUp className="text-gray-500" />
          ) : (
            <FaChevronDown className="text-gray-500" />
          )}
        </header>

        {expandedChannels[channelId] && (
          <article className="mt-4 px-6">
            {channelId === 'website-link' ? (
              <section className="mb-4">
                <input
                  type="text"
                  id="website-url"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your full website URL to link (e.g., https://www.example.com)"
                  value={websiteUrl}
                  onChange={handleWebsiteUrlChange}
                />
                <button
                  onClick={linkWebsite}
                  disabled={loading}
                  className={`mt-4 py-2 px-4 rounded-md text-white font-medium ${
                    loading ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'
                  }`}
                >
                  {loading ? 'Linking...' : 'Link Site'}
                </button>
                {scriptCode && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Integration Script</h4>
                    <pre className="bg-gray-50 p-4 rounded-md overflow-x-auto">
                      <code>{scriptCode}</code>
                    </pre>
                    <p className="mt-2 text-sm text-gray-600">Add this script to your website to integrate.</p>
                  </div>
                )}
              </section>
            ) : (
              <>
                <section className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Saved Parameters</h4>
                  {(savedParams[channelId] || []).map((param, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center bg-gray-50 p-2 rounded-md mb-2"
                    >
                      <span className="flex-1 text-sm">{param.configType}</span>
                      <span className="flex-1 text-sm">{param.accountNumber}</span>
                      <span className="flex-1 text-sm max-w-xs flex-wrap break-words">{param.token}</span>
                      <span className="flex-1 text-sm">
                        {param.valid ? <FaCheck className="text-green-500" /> : <FaTimes className="text-red-500" />}
                      </span>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => editParam(channelId, index)}
                          className="text-blue-500 hover:text-blue-700"
                          title="Edit"
                        >
                          <FaPencilAlt />
                        </button>
                        <button
                          onClick={() => deleteParam(channelId, index)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  ))}
                </section>
                <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <label htmlFor={`${channelId}-config`} className="block text-sm font-medium text-gray-700">
                    Config Type
                    <select
                      id={`${channelId}-config`}
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {configOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label htmlFor={`${channelId}-account`} className="block text-sm font-medium text-gray-700">
                    {channelId === 'email' ? 'Account' : 'Account Number'}
                    <input
                      type="text"
                      id={`${channelId}-account`}
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={channelId === 'email' ? 'Enter email address' : 'Enter account number'}
                    />
                  </label>
                  <label htmlFor={`${channelId}-token`} className="block text-sm font-medium text-gray-700">
                    Token
                    <input
                      type="text"
                      id={`${channelId}-token`}
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Paste token here"
                    />
                  </label>
                </section>
                <button
                  onClick={() => saveParams(channelId)}
                  className="mt-4 bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 font-medium"
                >
                  Save
                </button>
              </>
            )}
          </article>
        )}
      </section>
    );
  }, [allowedParams, savedParams, expandedChannels, toggleChannel, linkWebsite, websiteUrl, scriptCode, loading, editParam, deleteParam, saveParams]);

  // Channel configuration data with icons and colors
  const channelConfig = [
    {
      tab: 'channels',
      channels: [
        { id: 'website-link', title: 'Link Website', icon: FaPlug, color: 'text-gray-600' },
        { id: 'twitter', title: 'Twitter (X)', icon: FaTwitter, color: 'text-sky-500' },
        { id: 'facebook', title: 'Facebook', icon: FaFacebook, color: 'text-blue-600' },
        { id: 'whatsapp', title: 'WhatsApp Biz Cloud', icon: FaWhatsapp, color: 'text-green-500' },
        { id: 'twillio', title: 'Twilio', icon: FaPhone, color: 'text-red-500' },
        { id: 'telegram', title: 'Telegram', icon: FaTelegram, color: 'text-blue-400' },
        { id: 'discord', title: 'Discord', icon: FaDiscord, color: 'text-indigo-500' },
        { id: 'microsoft_teams', title: 'MS Teams', icon: FaMicrosoft, color: 'text-blue-700' },
        { id: 'email', title: 'Email', icon: FaEnvelope, color: 'text-gray-600' },
        { id: 'sms_channel', title: 'SMS', icon: FaPhone, color: 'text-gray-600' },
      ],
    },
    {
      tab: 'inbound',
      channels: [
        { id: 'inbound_api', title: 'APIs & Custom Gateway', icon: FaPlug, color: 'text-gray-600' },
        { id: 'ecrm', title: 'eCRM', icon: FaDatabase, color: 'text-gray-600' },
        { id: 'helpdesk', title: 'Helpdesk & Ticketing', icon: FaTicketAlt, color: 'text-gray-600' },
        { id: 'voice_assistant', title: 'Voice Assistants', icon: FaRobot, color: 'text-gray-600' },
      ],
    },
    {
      tab: 'outbound',
      channels: [
        { id: 'cbs', title: 'Core Business System', icon: FaCogs, color: 'text-gray-600' },
        { id: 'erp', title: 'Enterprise Resource Planning', icon: FaDatabase, color: 'text-gray-600' },
        { id: 'custom_database', title: 'Custom Database & Warehouse', icon: FaDatabase, color: 'text-gray-600' },
        { id: 'outbound_api', title: 'Outbound APIs', icon: FaPlug, color: 'text-gray-600' },
        { id: 'mpesa_daraja', title: 'MPESA Daraja API', icon: FaMoneyBillWave, color: 'text-green-600' },
        { id: 'paypal', title: 'Paypal', icon: FaMoneyBillWave, color: 'text-blue-500' },
      ],
    },
    {
      tab: 'llm-ai',
      channels: [
        { id: 'ai_open_ai', title: 'ChatGPT by OpenAI', icon: FaBrain, color: 'text-gray-600' },
        { id: 'ai_deepseek', title: 'DeepSeek AI', icon: FaBrain, color: 'text-gray-600' },
        { id: 'ai_xai', title: 'xAI', icon: FaBrain, color: 'text-gray-600' },
        { id: 'ai_gemini', title: 'Gemini (Google DeepMind)', icon: FaBrain, color: 'text-gray-600' },
      ],
    },
  ];

  if (loading) return <div className="text-center p-6">Loading configurations...</div>;
  if (error) return <div className="text-center p-6 text-red-500">Error: {error}</div>;

  return (
    <main className="bg-gray-100 min-h-screen font-sans">
      <div className="container mx-auto p-6">
        <nav className="mb-4">
          <ul className="flex border-b">
            {channelConfig.map(({ tab }) => (
              <li key={tab} className="mr-1">
                <button
                  className={`inline-block py-2 px-4 font-semibold border-b-2 ${
                    activeTab === tab ? 'text-blue-500 border-blue-500' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <DialogBox
          isOpen={dialog.isOpen}
          title={dialog.title}
          message={dialog.message}
          status={dialog.status}
          content={dialog.content}
          onConfirm={dialog.onConfirm || closeDialog}
          onCancel={closeDialog}
          onClose={closeDialog}
        />

        <section className="bg-white rounded-lg shadow">
          {channelConfig.map(({ tab, channels }) => (
            activeTab === tab && (
              <React.Fragment key={tab}>
                <h2 className="text-2xl font-semibold p-6 pb-0">
                  {tab.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} Configuration
                </h2>
                <div className="p-6">
                  {channels.map(({ id, title, icon, color }) => (
                    <ChannelItem key={id} channelId={id} title={title} icon={icon} color={color} />
                  ))}
                </div>
              </React.Fragment>
            )
          ))}
        </section>
      </div>
    </main>
  );
};

export default IntegrationsConfig;
